import { START_DATE_RANGE_SELECT_OPTIONS_FULL } from "@/constants/constants";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getPreviousDateRange,
  getStartDateRange,
  getTimerangeFromDates,
  shouldHidePreset,
} from "@/services/DateRangeServices";
import { toast } from "sonner";
import useSelectedStores from "./use-selected-stores";
import { usePresets } from "@lib/api-hooks/usePresets/usePresets";
import { useDateRangeState, useAttributionPresetState } from "./url-state";
import {
  getMostAheadTimezone,
  getTodayMaxDate,
  formatDateInTimezone,
} from "@lib/util-functions/dates";

type Props = {
  maxDate?: Date;
  minDate?: Date;
  initialTimeRange?: {
    startDate: Date;
    endDate: Date;
    timerange: (typeof START_DATE_RANGE_SELECT_OPTIONS_FULL)[number]["value"];
  };
};

export const useInitialTimeRange = ({
  maxDate: maxDateFromProps,
  initialTimeRange,
  minDate,
}: Props) => {
  const { selectedOrganisation, selectedStores } = useSelectedStores();
  const { data: presets = [] } = usePresets(selectedOrganisation ?? "", {
    enabled: Boolean(selectedOrganisation),
  });
  const { attributionPreset } = useAttributionPresetState();
  const selectedPreset = presets?.find(
    (preset) => preset.presetId === attributionPreset
  );
  const isPresetRecalculating = selectedPreset?.status === "REPROCESSING";

  // Get the most ahead timezone from selected stores (needed for timerange calculations)
  const timezone = useMemo(
    () => getMostAheadTimezone(selectedStores),
    [selectedStores]
  );

  // Use provided maxDate (already timezone-aware from dashboard) or fallback to store's "today"
  const effectiveMaxDate = maxDateFromProps ?? getTodayMaxDate(timezone);

  // If preset is recalculating, use the earlier of recalculation date or effective maxDate
  const maxDateFromRecalculation = isPresetRecalculating
    ? dayjs(selectedPreset?.lastSetToProcessingAt).toDate()
    : undefined;
  const maxDate =
    isPresetRecalculating &&
    maxDateFromRecalculation &&
    dayjs(maxDateFromRecalculation).isBefore(effectiveMaxDate)
      ? maxDateFromRecalculation
      : effectiveMaxDate;
  const {
    startTime,
    endTime,
    timerange: timeRange,
    comparePeriod,
    setAllDates,
  } = useDateRangeState();
  const [dashboardInitialized, setDashboardInitialized] = useState(false);
  const [initialTimeRangeIsSet, setInitialTimeRangeIsSet] = useState(false);
  const toastShownRef = useRef(false);

  useEffect(() => {
    // when we change routes we want to wait the first render so that atoms can be initialized
    // and only then we can set the initial time range
    setDashboardInitialized(true);
  }, []);

  useEffect(() => {
    if (!dashboardInitialized) return;

    let timeRangeAdjusted = false;

    // Track what needs to be updated - we'll batch ALL updates (dates + comparison) at the end
    let finalStartDate = startTime;
    let finalEndDate = endTime;
    let finalTimerange = timeRange;
    let finalPreviousStartDate: string | null = null;
    let finalPreviousEndDate: string | null = null;
    let needsUpdate = false;

    // Step 1: Clamp dates to maxDate if they exceed it
    if (dayjs(maxDate).isBefore(startTime, "day")) {
      finalStartDate = dayjs(maxDate).format("YYYY-MM-DD");
      needsUpdate = true;
    }
    if (dayjs(maxDate).isBefore(endTime, "day")) {
      finalEndDate = dayjs(maxDate).format("YYYY-MM-DD");
      needsUpdate = true;
    }

    // Step 2: Validate that startDate is not after endDate
    if (dayjs(finalStartDate).isAfter(dayjs(finalEndDate))) {
      let correctedDates;
      // Try to use the current timerange if it's a valid preset
      if (
        timeRange !== "Custom" &&
        !shouldHidePreset(timeRange, maxDate, undefined, timezone)
      ) {
        correctedDates = getStartDateRange(timeRange, undefined, timezone);
      } else {
        // Fallback to initialTimeRange if provided, otherwise last 7 days
        const fallbackTimerange = initialTimeRange?.timerange ?? "last_7_days";
        correctedDates = initialTimeRange
          ? {
              startDate: initialTimeRange.startDate,
              endDate: initialTimeRange.endDate,
              key: "selection",
            }
          : getStartDateRange("last_7_days", undefined, timezone);
        finalTimerange = fallbackTimerange;
      }
      finalStartDate = formatDateInTimezone(correctedDates.startDate, timezone);
      finalEndDate = formatDateInTimezone(correctedDates.endDate, timezone);
      needsUpdate = true;
      timeRangeAdjusted = true;
    }

    // Step 3: Detect/validate the timerange
    let newTimerange = getTimerangeFromDates({
      startDate: finalStartDate,
      endDate: finalEndDate,
      timezone,
    });

    // If detected timerange is invalid, use initialTimeRange or fall back to last 7 days
    if (
      newTimerange !== "Custom" &&
      shouldHidePreset(newTimerange, maxDate, undefined, timezone)
    ) {
      if (
        initialTimeRange &&
        !shouldHidePreset(
          initialTimeRange.timerange,
          maxDate,
          undefined,
          timezone
        )
      ) {
        newTimerange = initialTimeRange.timerange as typeof newTimerange;
        finalStartDate = formatDateInTimezone(
          initialTimeRange.startDate,
          timezone
        );
        finalEndDate = formatDateInTimezone(initialTimeRange.endDate, timezone);
      } else {
        const last7Days = getStartDateRange("last_7_days", undefined, timezone);
        newTimerange = "last_7_days";
        finalStartDate = formatDateInTimezone(last7Days.startDate, timezone);
        finalEndDate = formatDateInTimezone(last7Days.endDate, timezone);
      }
      needsUpdate = true;
    }
    finalTimerange = newTimerange;

    // Step 4: Handle initial time range setup
    if (!initialTimeRangeIsSet) {
      if (maxDate) {
        const maxDateIsBeforeStartTime = dayjs(maxDate).isBefore(
          startTime,
          "day"
        );
        const maxDateIsBeforeEndTime = dayjs(maxDate).isBefore(endTime, "day");

        if (maxDateIsBeforeStartTime || maxDateIsBeforeEndTime) {
          timeRangeAdjusted = true;
          needsUpdate = true;

          if (
            maxDateIsBeforeStartTime &&
            maxDateIsBeforeEndTime &&
            initialTimeRange
          ) {
            finalStartDate = formatDateInTimezone(
              initialTimeRange.startDate,
              timezone
            );
            finalEndDate = formatDateInTimezone(
              initialTimeRange.endDate,
              timezone
            );
            finalTimerange =
              initialTimeRange.timerange as typeof finalTimerange;

            // Validate the initial timerange before setting it
            if (
              finalTimerange !== "Custom" &&
              shouldHidePreset(finalTimerange, maxDate, undefined, timezone)
            ) {
              const last7Days = getStartDateRange(
                "last_7_days",
                undefined,
                timezone
              );
              finalTimerange = "last_7_days";
              finalStartDate = formatDateInTimezone(
                last7Days.startDate,
                timezone
              );
              finalEndDate = formatDateInTimezone(last7Days.endDate, timezone);
            }
          }
        }
      }

      // Calculate comparison dates for initial setup
      if (comparePeriod !== "custom") {
        const previousDates = getPreviousDateRange(
          comparePeriod ?? "last_period",
          { startDate: finalStartDate, endDate: finalEndDate }
        );
        finalPreviousStartDate = formatDateInTimezone(
          previousDates.startDate,
          timezone
        );
        finalPreviousEndDate = formatDateInTimezone(
          previousDates.endDate,
          timezone
        );
        needsUpdate = true;
      }

      setInitialTimeRangeIsSet(true);
    }

    // Step 5: For non-Custom timeranges, sync dates with the timerange preset
    if (finalTimerange !== "Custom") {
      if (shouldHidePreset(finalTimerange, maxDate, undefined, timezone)) {
        // If the timerange is now invalid, use initialTimeRange or fall back to last 7 days
        if (
          initialTimeRange &&
          !shouldHidePreset(
            initialTimeRange.timerange,
            maxDate,
            undefined,
            timezone
          )
        ) {
          finalTimerange = initialTimeRange.timerange as typeof finalTimerange;
          finalStartDate = formatDateInTimezone(
            initialTimeRange.startDate,
            timezone
          );
          finalEndDate = formatDateInTimezone(
            initialTimeRange.endDate,
            timezone
          );
        } else {
          const last7Days = getStartDateRange(
            "last_7_days",
            undefined,
            timezone
          );
          finalTimerange = "last_7_days";
          finalStartDate = formatDateInTimezone(last7Days.startDate, timezone);
          finalEndDate = formatDateInTimezone(last7Days.endDate, timezone);
        }
        needsUpdate = true;
        timeRangeAdjusted = true;
      } else {
        const datesFromTimerange = getStartDateRange(
          finalTimerange,
          undefined,
          timezone
        );
        let datesChanged = false;

        // Check if end date needs updating
        if (
          !dayjs(datesFromTimerange.endDate).isSame(finalEndDate, "day") &&
          !dayjs(maxDate).isBefore(datesFromTimerange.endDate, "day")
        ) {
          finalEndDate = formatDateInTimezone(
            datesFromTimerange.endDate,
            timezone
          );
          datesChanged = true;
        } else if (dayjs(maxDate).isBefore(finalEndDate, "day")) {
          finalEndDate = dayjs(maxDate).format("YYYY-MM-DD");
          finalTimerange = "Custom";
          datesChanged = true;
          timeRangeAdjusted = true;
        }

        // Check if start date needs updating
        if (
          !dayjs(datesFromTimerange.startDate).isSame(
            dayjs(finalStartDate),
            "day"
          ) &&
          !dayjs(maxDate).isBefore(datesFromTimerange.startDate, "day")
        ) {
          finalStartDate = formatDateInTimezone(
            datesFromTimerange.startDate,
            timezone
          );
          datesChanged = true;
        } else if (dayjs(maxDate).isBefore(finalStartDate, "day")) {
          finalTimerange = "Custom";
          finalStartDate = dayjs(maxDate).format("YYYY-MM-DD");
          datesChanged = true;
          timeRangeAdjusted = true;
        }

        if (datesChanged) {
          needsUpdate = true;
          // Also update comparison dates when main dates change
          if (comparePeriod !== "custom") {
            const previousDates = getPreviousDateRange(
              comparePeriod ?? "last_period",
              { startDate: finalStartDate, endDate: finalEndDate }
            );
            finalPreviousStartDate = dayjs(previousDates.startDate).format(
              "YYYY-MM-DD"
            );
            finalPreviousEndDate = dayjs(previousDates.endDate).format(
              "YYYY-MM-DD"
            );
          }
        }
      }
    } else if (
      initialTimeRangeIsSet &&
      maxDate &&
      (dayjs(maxDate).isBefore(startTime, "day") ||
        dayjs(maxDate).isBefore(endTime, "day"))
    ) {
      // Recalculate previous dates when maxDate moves backwards for Custom timeranges
      if (comparePeriod !== "custom") {
        const previousDates = getPreviousDateRange(
          comparePeriod ?? "last_period",
          { startDate: finalStartDate, endDate: finalEndDate }
        );
        finalPreviousStartDate = dayjs(previousDates.startDate).format(
          "YYYY-MM-DD"
        );
        finalPreviousEndDate = dayjs(previousDates.endDate).format(
          "YYYY-MM-DD"
        );
        needsUpdate = true;
      }
    }

    // Step 6: Apply ALL updates atomically (date range + comparison dates)
    if (needsUpdate) {
      const updateParams: Parameters<typeof setAllDates>[0] = {};

      if (finalStartDate !== startTime) {
        updateParams.startTime = finalStartDate;
      }
      if (finalEndDate !== endTime) {
        updateParams.endTime = finalEndDate;
      }
      if (finalTimerange !== timeRange) {
        updateParams.timerange = finalTimerange;
      }
      if (finalPreviousStartDate !== null) {
        updateParams.previousStartTime = finalPreviousStartDate;
      }
      if (finalPreviousEndDate !== null) {
        updateParams.previousEndTime = finalPreviousEndDate;
      }

      if (Object.keys(updateParams).length > 0) {
        setAllDates(updateParams);
      }
    }

    if (timeRangeAdjusted && !toastShownRef.current) {
      toast.info("Time range updated", {
        description:
          "The selected time range was adjusted because it exceeded the available data.",
        id: "timerange-adjustment",
      });
      toastShownRef.current = true;
    }
  }, [
    comparePeriod,
    dashboardInitialized,
    endTime,
    initialTimeRange,
    initialTimeRangeIsSet,
    maxDate,
    setAllDates,
    startTime,
    timeRange,
    timezone,
  ]);

  return {
    initialTimeRangeIsSet,
    setInitialTimeRangeIsSet,
  };
};
